// src/pages/FileRequests.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, formatDate, formatBytes, timeUntil, deletedLabel } from '../utils/api';
import JSZip from 'jszip';

function StatusBadge({ status }) {
  const map = {
    pending:   ['status-active',              '● Pending'],
    fulfilled: ['status-expired-downloaded',  '✓ Completed'],
    expired:   ['status-expired-timeout',     '⏱ Expired'],
    deleted:   ['status-deleted',             '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function DownloadModal({ request, onClose }) {
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    apiFetch(`requests/${request.id}/download`)
      .then(d => { setFiles(d.files || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [request.id]);

  const downloadAll = async () => {
    setDownloading(true);
    try {
      if (files.length === 1) {
        setProgress('Downloading…');
        const a = document.createElement('a'); a.href = files[0].url; a.download = files[0].name; a.click();
      } else {
        const zip = new JSZip();
        let i = 0;
        for (const file of files) {
          i++;
          setProgress(`Downloading file ${i} of ${files.length}…`);
          const resp = await fetch(file.url);
          zip.file(file.name, await resp.blob());
        }
        setProgress('Creating ZIP…');
        const blob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${request.title || 'files'}.zip`; a.click(); URL.revokeObjectURL(a.href);
      }
      setDone(true); setProgress('');
    } catch (e) { setError(e.message); }
    finally { setDownloading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '480px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>⬇️ Download — {request.title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: '32px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : error ? <div className="alert alert-error">❌ {error}</div>
          : done ? <div className="alert alert-success">✅ Download started.</div>
          : (
            <>
              <div style={{ marginBottom: '14px', fontSize: '13px', color: 'var(--g600)' }}>Submitted by <strong>{request.recipientEmail}</strong> on {formatDate(request.fulfilledAt)}</div>
              <div className="file-download-list" style={{ marginBottom: '16px' }}>
                {files.map((f, i) => (
                  <div key={i} className="download-item">
                    <div className="download-item-info">
                      <div className="download-item-name">📄 {f.name}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={downloadAll} disabled={downloading}>
                {downloading ? (progress || 'Preparing…') : files.length > 1 ? `⬇️ Download all ${files.length} files as ZIP` : '⬇️ Download file'}
              </button>
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function RequestCard({ request, onRefresh, showToast }) {
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);
  const isPending   = request.status === 'pending';
  const isFulfilled = request.status === 'fulfilled';
  const isExpired   = request.status === 'expired';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  return (
    <div className={`transfer-card${!isPending && !isFulfilled ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">📥 {request.title}</div>
          <StatusBadge status={request.status} />
        </div>
        <div className="transfer-meta">
          <span>→ {request.recipientEmail}</span>
          <span>📅 {formatDate(request.createdAt)}</span>
          {isPending && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(request.expiresAt)}</span>}
          {isFulfilled && <span style={{ color: 'var(--green)' }}>✓ Submitted {formatDate(request.fulfilledAt)}</span>}
          {request.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(request)}</span>}
          {request.message && <span style={{ color: 'var(--g500)', fontStyle: 'italic' }}>"{request.message}"</span>}
        </div>
      </div>
      <div style={{ padding: '10px 18px', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isFulfilled && (
          <button className="btn btn-primary btn-sm" onClick={() => setModal('download')} disabled={!!busy}>⬇️ Download files</button>
        )}
        {isPending && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('extend', () => apiFetch(`requests/${request.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days')}>⏱ Extend 3 days</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => action('expire', () => apiFetch(`requests/${request.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Expired')}>✕ Cancel</button>
          </>
        )}
        {isExpired && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('extend', () => apiFetch(`requests/${request.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-sent')}>🔄 Re-activate (3 days)</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => { if (!window.confirm('Delete this request?')) return; action('delete', () => apiFetch(`requests/${request.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted'); }}>🗑 Delete</button>
          </>
        )}
      </div>
      {modal === 'download' && <DownloadModal request={request} onClose={() => setModal(null)} />}
    </div>
  );
}

export default function FileRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await apiFetch('requests/list?type=file'); setRequests(d.requests || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const pending   = requests.filter(r => r.status === 'pending');
  const fulfilled = requests.filter(r => r.status === 'fulfilled');
  const deleted   = requests.filter(r => r.status === 'deleted');
  const expired   = requests.filter(r => !['pending','fulfilled','deleted'].includes(r.status));
  const shown = tab === 'pending' ? pending : tab === 'fulfilled' ? fulfilled : tab === 'deleted' ? deleted : expired;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="page-eyebrow">Files</div>
          <h1 className="page-title">📥 File Requests</h1>
          <p className="page-subtitle">Request files from external parties — they receive a secure single-use upload link</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/filerequests/new')}>📥 New File Request</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card stat-card-red"><div className="stat-icon stat-icon-red">📥</div><div className="stat-info"><div className="stat-value">{requests.length}</div><div className="stat-label">Total</div></div></div>
        <div className="stat-card stat-card-green"><div className="stat-icon stat-icon-green">●</div><div className="stat-info"><div className="stat-value">{pending.length}</div><div className="stat-label">Pending</div></div></div>
        <div className="stat-card stat-card-blue"><div className="stat-icon stat-icon-blue">✓</div><div className="stat-info"><div className="stat-value">{fulfilled.length}</div><div className="stat-label">Completed</div></div></div>
        <div className="stat-card stat-card-amber"><div className="stat-icon stat-icon-amber">⏱</div><div className="stat-info"><div className="stat-value">{expired.length}</div><div className="stat-label">Expired</div></div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>Pending <span className="tab-badge">{pending.length}</span></button>
            <button className={`tab${tab === 'fulfilled' ? ' active' : ''}`} onClick={() => setTab('fulfilled')}>Completed <span className="tab-badge">{fulfilled.length}</span></button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>Expired <span className="tab-badge">{expired.length}</span></button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>Deleted <span className="tab-badge">{deleted.length}</span></button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
        </div>
        <div className="card-body">
          {loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : shown.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📥</div>
              <div className="empty-state-title">{tab === 'pending' ? 'No pending requests' : tab === 'fulfilled' ? 'No completed requests yet' : tab === 'deleted' ? 'No deleted requests' : 'No expired requests'}</div>
              {tab === 'pending' && <><div className="empty-state-desc" style={{ marginBottom: '12px' }}>Send a secure upload link to an external party.</div><button className="btn btn-primary" onClick={() => navigate('/filerequests/new')}>Create your first request</button></>}
            </div>
          ) : (
            <div className="transfer-list">
              {shown.map(r => <RequestCard key={r.id} request={r} onRefresh={load} showToast={showToast} />)}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
